import { RenameDialog } from '@/components/RenameDialog/RenameDialog'
import { UploadModal } from '@/components/UploadModal/UploadModal'
import { Button, cn, Icon } from '@flatfile/baseline'
import { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { Artifact } from './Header'

export type File = {
  id: string
  name: string
}

export type TabItem = {
  id: string
  name: string
  type: 'file' | 'artifact'
  artifactType?: Artifact['type']
  data?: any
}

interface TabProps {
  item: TabItem
  isActive: boolean
  onTabClick: (item: TabItem) => void
  onRename?: (itemId: string, newName: string) => Promise<void>
}

const Tab = ({ item, isActive, onTabClick, onRename }: TabProps) => {
  const [isRenaming, setIsRenaming] = useState(false)

  const handleRename = async (newName: string) => {
    if (onRename && item.type === 'file') {
      await onRename(item.id, newName)
    }
  }

  const getIcon = () => {
    if (item.type === 'artifact') {
      return 'analytics' // Or use different icons based on artifactType
    }
    return 'file-bend'
  }

  const getIconBgColor = () => {
    if (item.type === 'artifact') {
      return 'bg-purple-100' // Different color for artifacts
    }
    return 'bg-accent'
  }

  return (
    <div
      className={cn('flex items-center px-1 max-w-[200px] overflow-hidden', {
        'border-t border-l border-r rounded-t-md z-10 bg-popover mt-1 -mb-[1px] flex-grow-0':
          isActive,
        'mx-[1px]': !isActive,
      })}
    >
      {item.type === 'file' && onRename ? (
        <RenameDialog
          isOpen={isRenaming}
          onOpenChange={setIsRenaming}
          currentName={item.name}
          onRename={handleRename}
          title='File Name'
          trigger={
            <Button
              variant='ghost'
              className={cn('truncate h-8 px-2 flex items-center gap-2', {
                'mb-1': isActive,
              })}
              onClick={() => onTabClick(item)}
              onDoubleClick={() => setIsRenaming(true)}
              style={{ minWidth: 0, justifyContent: 'flex-start' }}
            >
              <span className='flex items-center gap-2'>
                <span
                  className={cn(
                    'rounded w-5 h-5 flex items-center justify-center',
                    getIconBgColor()
                  )}
                >
                  <Icon name={getIcon()} />
                </span>
                <span className='truncate max-w-[140px] text-left'>
                  {item.name}
                </span>
              </span>
            </Button>
          }
        />
      ) : (
        <Button
          variant='ghost'
          className={cn('truncate h-8 px-2 flex items-center gap-2', {
            'mb-1': isActive,
          })}
          onClick={() => onTabClick(item)}
          style={{ minWidth: 0, justifyContent: 'flex-start' }}
        >
          <span className='flex items-center gap-2'>
            <span
              className={cn(
                'rounded w-5 h-5 flex items-center justify-center',
                getIconBgColor()
              )}
            >
              <Icon name={getIcon()} />
            </span>
            <span className='truncate max-w-[140px] text-left'>
              {item.name}
            </span>
          </span>
        </Button>
      )}
    </div>
  )
}

export const FileTabs = ({
  files,
  artifacts,
  onRename,
}: {
  files?: File[]
  artifacts?: Artifact[]
  onRename?: (fileId: string, newName: string) => Promise<void>
}) => {
  const { projectId, fileId, artifactId } = useParams<{
    projectId: string
    fileId: string
    artifactId: string
  }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const [showUploadModal, setShowUploadModal] = useState(false)
  const navigate = useNavigate()

  // Combine files and artifacts into a single list of tabs
  const tabs = useMemo(() => {
    const items: TabItem[] = []

    // Add files
    if (files) {
      files.forEach((file) => {
        items.push({
          id: file.id,
          name: file.name,
          type: 'file' as const,
        })
      })
    }

    // Add artifacts
    if (artifacts) {
      artifacts.forEach((artifact) => {
        items.push({
          id: artifact.id,
          name: artifact.name || 'Analysis Report',
          type: 'artifact' as const,
          artifactType: artifact.type,
          data: artifact.data,
        })
      })
    }

    return items
  }, [files, artifacts])

  const handleTabClick = (item: TabItem) => {
    if (item.type === 'file') {
      // Navigate to file route
      navigate(`/projects/${projectId}/files/${item.id}/prepare-chat`, {
        replace: true,
      })
    } else {
      // Navigate to artifact route
      navigate(`/projects/${projectId}/artifacts/${item.id}/prepare-chat`, {
        replace: true,
      })
    }
  }

  const [activeItem, setActiveItem] = useState<TabItem | null>(null)

  useEffect(() => {
    if (artifactId) {
      setActiveItem(
        tabs.find(
          (item) => item.id === artifactId && item.type === 'artifact'
        ) ?? null
      )
    } else if (fileId) {
      setActiveItem(
        tabs.find((item) => item.id === fileId && item.type === 'file') ??
          tabs[0]
      )
    } else {
      setActiveItem(tabs[0])
    }
  }, [fileId, artifactId, tabs])

  return (
    <div className='flex -mb-[1px] h-full'>
      {tabs.map((item, index) => (
        <Tab
          key={`tab-${item.type}-${item.id}-${index}`}
          item={item}
          isActive={
            item.id === activeItem?.id && item.type === activeItem?.type
          }
          onTabClick={handleTabClick}
          onRename={item.type === 'file' ? onRename : undefined}
        />
      ))}
      <Button
        variant='ghost'
        size='sm'
        className='ml-2 mt-3 mb-3 p-1'
        onClick={() => setShowUploadModal(true)}
      >
        <Icon name='plus-large' />
      </Button>

      {/* Upload Modal */}
      {projectId && (
        <UploadModal
          isOpen={showUploadModal}
          onOpenChange={setShowUploadModal}
          projectId={projectId}
        />
      )}
    </div>
  )
}
